<template>
  <div class="tagSelect">
    <div class="tagSelect-control">
      <div class="tagSelect-control-selected"
        tabindex="1"
        @click="onControlClick"
        @blur="onBlur"
      >
        <span>{{ placeholder }}</span>
        <neb-icon name="caretDown"
          height="12px"
          width="12px"
        ></neb-icon>
      </div>
      <div class="tagSelect-control-dropdown"
        v-show="isOpen"
      >
        <div class="tagSelect-control-dropdown-search">
          <neb-input icon="search"
            @input="onSearchInput"
            @blur="onBlur"
            placeholder="Search"
          />
        </div>
        <div class="tagSelect-control-dropdown-options"
          v-loading="optionsLoading"
        >
          <div class="tagSelect-control-dropdown-noOptions"
            v-show="options.length < 1"
          >
            <neb-icon name="x"
              height="40px"
              width="40px"
            />
            <span>No Options</span>
          </div>
          <div class="tagSelect-control-dropdown-option"
            v-for="option in options"
            :key="option.value"
            @click="() => onOptionClick(option.value)"
          >
            <span>{{ option.label }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import NebInput from '@/components/Nebula/controls/Input'
import NebIcon from '@/components/Nebula/Icon'
import { debounce } from 'lodash'

export default {
  name: 'TagSelect',

  components: {
    NebInput,
    NebIcon
  },

  props: {
    /**
     * Dropdown options.
     * @example [ { value: 1, label: 'One' }, ... ]
     */
    options: {
      type: Array,
      default: () => []
    },
    optionsLoading: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Select'
    }
  },

  emits: [
    'action:openDropdown',
    'action:closeDropdown',
    'input:select',
    'input:search'
  ],

  setup (props, { emit }) {
    const isOpen = ref(false)

    const onControlClick = () => {
      if (isOpen.value) {
        isOpen.value = false
        emit('action:closeDropdown')
      } else {
        isOpen.value = true
        emit('action:openDropdown')
      }
    }
    const onSearchInput = debounce(v => {
      emit('input:search', v)
    }, 600)
    const onOptionClick = v => {
      emit('input:select', v)
      isOpen.value = false
    }
    const onBlur = e => {
      if (e.relatedTarget === null) {
        setTimeout(() => isOpen.value = false, 200)
      }
    }

    return {
      isOpen,
      onControlClick,
      onSearchInput,
      onOptionClick,
      onBlur
    }
  }
}
</script>

<style lang="scss">
@import '../Nebula/style.scss';

.tagSelect {
     position: relative;
     user-select: none;

    &-control {
      position: relative;
      display: flex;
      flex-direction: column;

      &-selected {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;

        span {
          margin-right: 5px;
          font-family: $defaultFont;
          font-size: 12px;
          color: $brown-grey;
        }

        .nebIcon {
          color: $brown-grey;
        }
      }

      &-dropdown {
        position: absolute;
        display: block;
        top: 130%;
        left: 0;
        border: 1px solid $pinkish-grey;
        border-radius: 3px;
        background-color: white;
        z-index: 2;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
        max-height: 310px;
        overflow: hidden;
        min-width: 200px;

        &-noOptions {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100px;

          .nebIcon {
            color: $warm-grey;
          }

          span {
            display: block;
            padding-top: 5px;
            font-family: $defaultFont;
            font-size: 12px;
            color: $warm-grey;
          }
        }

        &-search {
          padding: 9px 15px;
          border-bottom: 1px solid $pinkish-grey;
          box-sizing: border-box;
        }

        &-options {
          overflow-x: hidden;
          overflow-y: scroll;
          max-height: 258px;
        }

        &-option {
          position: relative;
          display: block;
          cursor: pointer;
          transition: all 0.1s;
          padding: 9px 15px;

          span {
            font-size: 12px;
            font-family: $defaultFont;
            color: $slate-grey;
            white-space: nowrap;
          }

          &:hover {
            background-color: $duck-egg-blue;
          }
        }
      }
    }
}
</style>
